// WhatsApp reactions arrive as messages linked to the reacted message (inReplyTo).
// Like the WhatsApp app, they are shown as a badge on that message instead of a bubble of their own.

const isReaction = message => Boolean(message.contentAttributes?.isReaction);

const reactionTargetId = message => message.contentAttributes?.inReplyTo;

const senderKey = message =>
  `${message.messageType}:${message.senderId ?? message.sender?.id ?? ''}`;

/**
 * Splits the conversation messages into the ones to render and the reactions of each message.
 * A reaction only becomes a badge when the reacted message is loaded; otherwise it stays a bubble.
 * Each sender keeps only their latest reaction on a message, and removed reactions are dropped.
 * @param {Array} messages - Conversation messages in camelCase, oldest first
 * @returns {{ visibleMessages: Array, reactionsByMessageId: Object }}
 */
export const groupReactions = messages => {
  const loadedIds = new Set(messages.map(message => message.id));
  const latestBySender = {};

  const visibleMessages = messages.filter(message => {
    if (!isReaction(message)) return true;
    if (message.contentAttributes?.deleted) return false;

    const targetId = reactionTargetId(message);
    if (!loadedIds.has(targetId)) return true;

    latestBySender[targetId] = {
      ...latestBySender[targetId],
      [senderKey(message)]: message,
    };
    return false;
  });

  const reactionsByMessageId = Object.fromEntries(
    Object.entries(latestBySender).map(([targetId, bySender]) => [
      targetId,
      Object.values(bySender).map(message => ({
        id: message.id,
        emoji: message.content,
        senderName: message.sender?.name || '',
      })),
    ])
  );

  return { visibleMessages, reactionsByMessageId };
};
